import React, { useEffect, useState, useMemo, useRef } from 'react';
import { useItems } from '../contexts/ItemsContext';
import { useUser } from '../contexts/UserContext';
import { useNavigate } from 'react-router-dom';
import './Programs.css';
import { getFilters } from '../api/itemsApi';

const ItemCard = ({ item, onOpen, onToggleFavorite, isFavorite, showMinitsLabel }) => {
  const front = (item.files || []).find(f => f.for_show && f.type === 'image') || (item.files || []).find(f => f.type === 'image');

  const handleFavoriteClick = (e) => {
    e.stopPropagation();
    onToggleFavorite(item);
  };

  return (
    <button className="program-card" onClick={() => onOpen(item)}>
      <div className="program-card-border">
        {showMinitsLabel && (
          <div className="minits-label">minutes</div>
        )}
        <div className="program-card-media">
          {front ? <img src={front.url} alt={item.name} /> : <div className="program-card-placeholder">אין תמונה</div>}
          <button
            className={`favorite-btn ${isFavorite ? 'favorite-active' : ''}`}
            onClick={handleFavoriteClick}
            aria-label={isFavorite ? 'בסוף לא' : 'אהבתי'}
          >
            {isFavorite ? '❤️' : '🤍'}
          </button>
        </div>
          <div className="program-card-footer">

            <div className="program-card-name">{item.name}</div>
            <div className="program-card-meta">₪ {item.price} </div>
            <div className="program-card-meta"> {item.much_for}</div>
              {item.video && <div className="program-card-video">
                <svg width="26" height="26" viewBox="0 0 24 24" fill="none"
                    stroke="#0cad5e" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    xmlns="http://www.w3.org/2000/svg">
                    <circle cx="12" cy="12" r="10" />
                    <polygon points="10,8 16,12 10,16" />
                  </svg>  
                </div>}
         </div>
      </div>
    </button>
  );
};

// נציג גם תוכניות וגם מיניטס
const GENRES = ['תוכנית', 'מיניטס'];

const Programs = () => {
  const { itemsByGenre, loadItems, refreshItems, loadingByGenre, errorByGenre } = useItems();
  const { user, addFavorite, removeFavorite } = useUser();
  const navigate = useNavigate();
  // שימוש במערך קבוע GENRES
  const genres = GENRES;

  // filter state
  const [options, setOptions] = useState({ crowds: [], timings: [], types: [] });
  const [filtersLoading, setFiltersLoading] = useState(false);
  const [filtersError, setFiltersError] = useState(null);
  const [selected, setSelected] = useState({ crowds: new Set(), timings: new Set(), types: new Set(), screenshot: 'any' });

  // UI: open/close drop panels
  const [openFilter, setOpenFilter] = useState({ crowds: false, timings: false, types: false, screenshot: false });

  // refs לכל החלוניות
  const crowdsRef = useRef(null);
  const timingsRef = useRef(null);
  const typesRef = useRef(null);
  const screenshotRef = useRef(null);

  useEffect(() => {
    genres.forEach(g => loadItems(g));
  }, []);

  // נאסוף את כל הפריטים מכל הג'אנרים
  const items = genres.flatMap(g => itemsByGenre[g] || []);
  const loading = genres.some(g => loadingByGenre[g]);
  const error = genres.map(g => errorByGenre[g]).find(Boolean);

  const openItem = (item) => {
    navigate(`/item/${item.id}`);
  };

  const isItemFavorite = (item) => {
    if (!user?.likes) return false;
    return user.likes.some(like => like.id === item.id);
  };

  const toggleFavorite = async (item) => {
    if (!user) return;

    if (isItemFavorite(item)) {
      await removeFavorite(item.id);
    } else {
      await addFavorite(item);
    }
  };

  useEffect(() => {
    let cancelled = false;
    let abortController = new AbortController();
    setOptions({ crowds: [], timings: [], types: [] });
    setFiltersLoading(true);
    setFiltersError(null);

    const tryFetch = async () => {
      try {
        // קריאה ללא signal, כי getFilters לא תומך בפרמטרים
        const json = await getFilters();
        if (cancelled) return;
        const mk = arr => (Array.isArray(arr) ? arr.map(v => (typeof v === 'object' ? v.name || String(v) : String(v))) : []);
        setOptions({ crowds: mk(json.crowds), timings: mk(json.timings), types: mk(json.types) });
        setFiltersLoading(false);
      } catch (e) {
        if (!cancelled) {
          setOptions({ crowds: [], timings: [], types: [] });
          setFiltersError(e.message || 'failed');
          setFiltersLoading(false);
        }
        // אם זו ביטול יזום, לא להציג שגיאה
        if (e.name === 'AbortError') return;
        console.warn('Filters API not available, options cleared:', e.message || e);
      }
    };

    tryFetch();
    return () => {
      cancelled = true;
      abortController.abort();
    };
  }, [genres]);

  // סגירת חלוניות בלחיצה בחוץ
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        crowdsRef.current && !crowdsRef.current.contains(e.target) &&
        timingsRef.current && !timingsRef.current.contains(e.target) &&
        typesRef.current && !typesRef.current.contains(e.target) &&
        screenshotRef.current && !screenshotRef.current.contains(e.target)
      ) {
        setOpenFilter({ crowds: false, timings: false, types: false, screenshot: false });
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleValue = (setKey, value) => {
    setSelected(prev => {
      const copy = { ...prev };
      const set = new Set(prev[setKey]);
      if (set.has(value)) set.delete(value); else set.add(value);
      copy[setKey] = set;
      return copy;
    });
  };

  const setScreenshot = (mode) => {
    setSelected(prev => ({ ...prev, screenshot: mode }));
  };

  // פונקציה לפתיחת חלונית - סוגרת את כל האחרות
  const toggleFilterPanel = (filterName) => {
    setOpenFilter(prev => ({
      crowds: false,
      timings: false,
      types: false,
      screenshot: false,
      [filterName]: !prev[filterName]
    }));
  };

  const filteredItems = useMemo(() => {
    return items.filter(it => {
      if (selected.crowds.size > 0) {
        const itCrowds = (it.crowds || []).map(c => (typeof c === 'object' ? c.name || c.id : String(c)));
        const hasAny = Array.from(selected.crowds).some(s => itCrowds.includes(s));
        if (!hasAny) return false;
      }

      if (selected.timings.size > 0) {
        const itTimes = (it.timings || []).map(t => (typeof t === 'object' ? t.name || t.id : String(t)));
        const hasAny = Array.from(selected.timings).some(s => itTimes.includes(s));
        if (!hasAny) return false;
      }

      if (selected.types.size > 0) {
        const itTypes = (it.types || []).map(t => (typeof t === 'object' ? t.name || t.id : String(t)));
        const hasAny = Array.from(selected.types).some(s => itTypes.includes(s));
        if (!hasAny) return false;
      }

      if (selected.screenshot === 'with') {
        const hasImage = (it.files || []).some(f => f.type === 'image');
        if (!hasImage) return false;
      } else if (selected.screenshot === 'without') {
        const hasImage = (it.files || []).some(f => f.type === 'image');
        if (hasImage) return false;
      }

      return true;
    });
  }, [items, selected]);

  return (
    <div className="programs-container">
      <div className="programs-frame">
      <div className="programs-filters">
        <div className="filters-row">
    <button type="button" className="filter-clear" onClick={() => {
            setSelected({ crowds: new Set(), timings: new Set(), types: new Set(), screenshot: 'any' });
          }}>נקה מסננים</button>
          {/* Filter: crowds / audience */}
          <div className="filter" ref={crowdsRef}>
            <button type="button" className="filter-btn" onClick={() => toggleFilterPanel('crowds')}>
              <span style={{ color: 'hotpink' }}>v</span>
              {` קהל יעד | \u200E${selected.crowds.size > 0 ? selected.crowds.size : '0'}`}            </button>
            {openFilter.crowds && (
              <div className="filter-panel">
                {filtersLoading && <div className="filter-empty">חכי שניה, זה עולה...</div>}
                {filtersError && <div className="filter-empty">אופסססס, פשלה! איזה בושות! </div>}
                {!filtersLoading && !filtersError && options.crowds.length === 0 && <div className="filter-empty">אין אפשרויות</div>}
                {!filtersLoading && !filtersError && options.crowds.map(opt => (
                  <label key={opt} className="filter-option"><input type="checkbox" checked={selected.crowds.has(opt)} onChange={() => toggleValue('crowds', opt)} />{opt}</label>
                ))}
              </div>
            )}
          </div>

          {/* Filter: timings */}
          <div className="filter" ref={timingsRef}>
            <button
              type="button"
              className="filter-btn"
              onClick={() => toggleFilterPanel('timings')}
            >
              <span style={{ color: 'hotpink' }}>v</span>
              {` עיתויים | \u200E${selected.timings.size > 0 ? selected.timings.size : '0'}`}
            </button>
            {openFilter.timings && (
              <div className="filter-panel">
                {filtersLoading && <div className="filter-empty">עד שזה עולה, תני חיוך :)</div>}
                {filtersError && <div className="filter-empty">אופס, משהו כאן לא עבד</div>}
                {!filtersLoading && !filtersError && options.timings.length === 0 && <div className="filter-empty">אין אפשרויות</div>}
                {!filtersLoading && !filtersError && options.timings.map(opt => (
                  <label key={opt} className="filter-option"><input type="checkbox" checked={selected.timings.has(opt)} onChange={() => toggleValue('timings', opt)} />{opt}</label>
                ))}
              </div>
            )}
          </div>

          {/* Filter: types/styles */}
          <div className="filter" ref={typesRef}>
            <button type="button" className="filter-btn" onClick={() => toggleFilterPanel('types')}>
              <span style={{ color: 'hotpink' }}>v</span>
              {` סגנונות | \u200E${selected.types.size > 0 ? selected.types.size : '0'}`}            </button>
            {openFilter.types && (
              <div className="filter-panel">
                {filtersLoading && <div className="filter-empty">עד שזה עולה, תני חיוך :)</div>}
                {filtersError && <div className="filter-empty">אופס, משהו כאן לא עבד</div>}
                {!filtersLoading && !filtersError && options.types.length === 0 && <div className="filter-empty">אין אפשרויות</div>}
                {!filtersLoading && !filtersError && options.types.map(opt => (
                  <label key={opt} className="filter-option"><input type="checkbox" checked={selected.types.has(opt)} onChange={() => toggleValue('types', opt)} />{opt}</label>
                ))}
              </div>
            )}
          </div>

          {/* Filter: screenshots (with/without) */}
          <div className="filter" ref={screenshotRef}>
            <button type="button" className="filter-btn" onClick={() => toggleFilterPanel('screenshot')}>
              <span style={{ color: 'hotpink' }}>v</span>
              {` קטעי מסך `}  
            </button>
            {openFilter.screenshot && (
              <div className="filter-panel small">
                <label className="filter-option"><input type="radio" name="screenshot" checked={selected.screenshot === 'any'} onChange={() => setScreenshot('any')} /> הכל</label>
                <label className="filter-option"><input type="radio" name="screenshot" checked={selected.screenshot === 'with'} onChange={() => setScreenshot('with')} /> עם קטעי מסך</label>
                <label className="filter-option"><input type="radio" name="screenshot" checked={selected.screenshot === 'without'} onChange={() => setScreenshot('without')} /> בלי קטעי מסך</label>
              </div>
            )}
          </div>

        </div>
          <div class="line"></div>
        <div className="filters-actions">

          <div className="filters-count">
  מציג {filteredItems.length} מתוך {items.length}
  <span> | </span>
  מתלבטת מה מתאים לך?<a href="/contactus" className="contactus-inline-link"> את יכולה פשוט לדבר איתנו</a>
</div>
        </div>
      </div>
        {loading && <div className="programs-loading">...חכי שניה, זה עולה</div>}
        {error && <div className="programs-error"> :) אופססססס, פשלה! איזה בושות </div>}
        <div className="programs-grid">
          {filteredItems.map(it => (
            <ItemCard
              key={it.id}
              item={it}
              onOpen={openItem}
              onToggleFavorite={toggleFavorite}
              isFavorite={isItemFavorite(it)}
              showMinitsLabel={it.genre === 'מיניטס'}
            />
          ))}
        </div>

        {!loading && !error && items.length === 0 && <div className="programs-empty">חיפשנו, לא מצאנו כלום :(</div>}
      </div>
    </div>
  );
};

export default Programs;